import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Save, User, Lock, Eye, EyeOff } from 'lucide-react';
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { useAuth } from '@/context/AuthContext';
import { changePasswordSchema, type ChangePasswordFormData } from '@/utils/validators';
import FormField from '@/components/FormField';

export function Settings() {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name || '');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await usersApi.update(user!.id, { name });
      return res.data;
    },
    onSuccess: () => {
      if (user) {
        updateUser({ ...user, name: nameValue });
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Nombre actualizado exitosamente');
      setEditingName(false);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar nombre');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      toast.success('Contraseña cambiada exitosamente');
      reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al cambiar contraseña');
    },
  });

  const handleSaveName = () => {
    if (!nameValue.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    updateNameMutation.mutate(nameValue.trim());
  };

  const handleCancelName = () => {
    setNameValue(user?.name || '');
    setEditingName(false);
  };

  const onSubmitPassword = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  const roleLabels: Record<string, string> = {
    admin: 'Administrador',
    supervisor: 'Supervisor',
    technician: 'Técnico',
    viewer: 'Espectador',
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-surface-900">Configuración</h1>

      <div className="rounded-lg border border-surface-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-surface-200 px-6 py-4">
          <User className="h-5 w-5 text-surface-400" />
          <h2 className="text-lg font-semibold text-surface-900">Mi Perfil</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-surface-500">Correo Electrónico</label>
            <p className="mt-1 text-sm text-surface-900">{user?.email || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-500">Rol</label>
            <p className="mt-1 text-sm text-surface-900">
              {roleLabels[user?.role || ''] || user?.role || '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-500">Nombre</label>
            {editingName ? (
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="block flex-1 rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Tu nombre"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={updateNameMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                >
                  {updateNameMutation.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={handleCancelName}
                  className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <p className="text-sm text-surface-900">{user?.name || '-'}</p>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Editar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-surface-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-surface-200 px-6 py-4">
          <Lock className="h-5 w-5 text-surface-400" />
          <h2 className="text-lg font-semibold text-surface-900">Cambiar Contraseña</h2>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-4">
            <FormField label="Contraseña Actual" error={errors.currentPassword?.message} required>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  {...register('currentPassword')}
                  className="block w-full rounded-md border border-surface-300 bg-white pr-10 pl-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Contraseña actual"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-surface-400 hover:text-surface-600 transition-colors"
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Nueva Contraseña" error={errors.newPassword?.message} required>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  {...register('newPassword')}
                  className="block w-full rounded-md border border-surface-300 bg-white pr-10 pl-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Nueva contraseña"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-surface-400 hover:text-surface-600 transition-colors"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Confirmar Contraseña" error={errors.confirmPassword?.message} required>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  {...register('confirmPassword')}
                  className="block w-full rounded-md border border-surface-300 bg-white pr-10 pl-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Repite la nueva contraseña"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-surface-400 hover:text-surface-600 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <div className="pt-2">
              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {changePasswordMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Cambiando...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Cambiar Contraseña
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Settings;
